import { useCallback, useMemo, useState } from "react"
import { Button } from "@mui/material"
import { DataGridPremium as DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-premium"
import Card from "components/card"
import useConnections from "../../hooks/use-connections"
import type { UseConnectionsHook } from "../../hooks/use-connections"
import RevokeConfirmDialog from "./revoke-confirm-dialog"
import ConnectionsStatePanel from "./state-panel"
import type { OAuthConnection } from "../../types"
import "./style.scss"

interface ConnectionsProps {
  useConnectionsHook?: UseConnectionsHook
}

type ConnectionGridRow = Omit<OAuthConnection, "appName"> & {
  displayAppName: string
}

function formatDate(value: string | null | undefined) {
  if (!value) return "-"
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return "-"
  return parsedDate.toLocaleString()
}

function toTimestamp(value: string | null | undefined) {
  if (!value) return 0
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) return 0
  return parsedDate.getTime()
}

export default function Connections({ useConnectionsHook = useConnections }: ConnectionsProps) {
  const [connectionToDelete, setConnectionToDelete] = useState<OAuthConnection | null>(null)
  const [deletingConnectionIds, setDeletingConnectionIds] = useState<string[]>([])
  const [deletedConnectionIds, setDeletedConnectionIds] = useState<string[]>([])
  const { connections, isLoading, isError, refetch, deleteConnection } = useConnectionsHook()

  const visibleConnections = useMemo(
    () => connections.filter(({ id }) => !deletedConnectionIds.includes(id)),
    [connections, deletedConnectionIds],
  )
  const visibleConnectionsById = useMemo(
    () => new Map(visibleConnections.map((connection) => [connection.id, connection])),
    [visibleConnections],
  )
  const hasVisibleConnections = visibleConnections.length > 0
  const isEmptyStateVisible = !isLoading && !isError && !hasVisibleConnections
  const isTableVisible = !isLoading && !isError && hasVisibleConnections

  const rows = useMemo<ConnectionGridRow[]>(
    () =>
      visibleConnections.map((connection) => ({
        ...connection,
        displayAppName: connection.appName || "Unknown application",
      })),
    [visibleConnections],
  )

  const setDeleting = useCallback((connectionId: string, isDeleting: boolean) => {
    setDeletingConnectionIds((previousIds) => {
      const hasId = previousIds.includes(connectionId)
      if (isDeleting && !hasId) {
        return [...previousIds, connectionId]
      }

      if (!isDeleting && hasId) {
        return previousIds.filter((id) => id !== connectionId)
      }

      return previousIds
    })
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!connectionToDelete) return

    const deletingConnectionId = connectionToDelete.id
    setConnectionToDelete(null)
    setDeleting(deletingConnectionId, true)

    try {
      await deleteConnection(deletingConnectionId)
      setDeletedConnectionIds((previousIds) => {
        if (previousIds.includes(deletingConnectionId)) return previousIds
        return [...previousIds, deletingConnectionId]
      })
      await refetch()
    } catch {
      // Notification is handled by the hook on error.
    } finally {
      setDeleting(deletingConnectionId, false)
    }
  }, [connectionToDelete, deleteConnection, refetch, setDeleting])

  const columns = useMemo<GridColDef<ConnectionGridRow>[]>(
    () => [
      { field: "displayAppName", headerName: "Application", flex: 1, minWidth: 180 },
      {
        field: "createdAt",
        headerName: "Date Connected",
        flex: 1,
        minWidth: 170,
        renderCell: ({ value }: GridRenderCellParams<ConnectionGridRow, string>) => formatDate(value),
        sortComparator: (leftValue, rightValue) => toTimestamp(leftValue) - toTimestamp(rightValue),
      },
      {
        field: "expiresAt",
        headerName: "Expires",
        flex: 1,
        minWidth: 170,
        renderCell: ({ value }: GridRenderCellParams<ConnectionGridRow, string>) => formatDate(value),
        sortComparator: (leftValue, rightValue) => toTimestamp(leftValue) - toTimestamp(rightValue),
      },
      {
        field: "actions",
        headerName: "",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "right",
        headerAlign: "right",
        width: 110,
        minWidth: 110,
        maxWidth: 110,
        renderCell: ({ row }: GridRenderCellParams<ConnectionGridRow>) => {
          const isDeleting = deletingConnectionIds.includes(row.id)
          return (
            <Button
              color="primary"
              size="small"
              disabled={isDeleting}
              onClick={() => setConnectionToDelete(visibleConnectionsById.get(row.id) ?? null)}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )
        },
      },
    ],
    [deletingConnectionIds, visibleConnectionsById],
  )

  return <Card
    title="Connected Applications"
    className="connections"
    rightOptions={null}
    leftOptions={null}
    subHeader={null}
    fixed={false}
    noHeader={false}
    noSubHeaderPadding={false}
  >
    <div className="content modules_user-profile_components_connections">
      <ConnectionsStatePanel
        isLoading={isLoading}
        isError={isError}
        isEmpty={isEmptyStateVisible}
        onRetry={refetch}
      />

      {isTableVisible && (
        <div className="connections-table">
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableColumnResize
            hideFooter
            pagination={false}
            initialState={{
              sorting: {
                sortModel: [{ field: "expiresAt", sort: "desc" }],
              },
            }}
          />
        </div>
      )}
    </div>

    <RevokeConfirmDialog
      open={!!connectionToDelete}
      appName={connectionToDelete?.appName}
      onClose={() => setConnectionToDelete(null)}
      onConfirm={handleConfirmDelete}
    />
  </Card>
}
